import { z } from 'zod';
import type {
  GameEvent,
  GameMeta,
  GameState,
  MomentDecision,
  ProbabilityPoint,
  Side,
  WinProbModel,
} from '../contracts.js';
import {
  REGULATION_PERIODS,
  OVERTIME_SECONDS,
  PERIOD_SECONDS,
  formatClock,
  secondsRemaining,
  topScorer,
} from '../state.js';
import { linearScore, predictHomeWin, sigmoid } from './winprob.js';

/**
 * Moment detector parameters (trained in apps/lab/src/train.ts → models/detector.v1.json).
 *
 * `score` is a logistic model of P(this game ends in a clutch finish or a last-10-s lead
 * change/tie | state now), fitted on Q4/OT states of TRAINING games only. The detector
 * fires once, at the first Q4/OT event inside the time window whose score clears
 * `fireThreshold`; both thresholds are tuned on training games (see train.ts).
 */
export const DetectorModelSchema = z.object({
  version: z.string(),
  trainedAt: z.string(),
  winProbVersion: z.string(),
  features: z.array(z.string()),
  intercept: z.number(),
  coefficients: z.array(z.number()),
  fireThreshold: z.number().min(0).max(1),
  maxSecondsRemaining: z.number().positive(), // Q4 clock window; overtime is always in-window
  objective: z.string(),
  metrics: z.record(z.string(), z.number()),
  trainGames: z.number().int(),
});
export type DetectorModel = z.infer<typeof DetectorModelSchema>;

/** Game-clock seconds elapsed since tip-off (regulation + overtime). */
export function elapsedSeconds(period: number, clockSec: number): number {
  if (period <= REGULATION_PERIODS)
    return (period - 1) * PERIOD_SECONDS + (PERIOD_SECONDS - clockSec);
  return (
    REGULATION_PERIODS * PERIOD_SECONDS +
    (period - REGULATION_PERIODS - 1) * OVERTIME_SECONDS +
    (OVERTIME_SECONDS - clockSec)
  );
}

/**
 * How much one basket moves the game: P(home) if home scores 2 (ball to away) minus
 * P(home) if away scores 2 (ball to home). 0 = decided, larger = every play matters.
 */
export function leverage(model: WinProbModel, s: GameState): number {
  if (s.status === 'final') return 0;
  const homeScores = predictHomeWin(model, {
    ...s,
    homeScore: s.homeScore + 2,
    possession: 'away',
  });
  const awayScores = predictHomeWin(model, {
    ...s,
    awayScore: s.awayScore + 2,
    possession: 'home',
  });
  return Math.max(0, homeScores - awayScores);
}

type ScoreInput = { state: GameState; leverage: number };
const SCORE_FEATURES = {
  absMargin: ({ state }) => Math.abs(state.homeScore - state.awayScore),
  absMarginPerSqrtTime: ({ state }) =>
    Math.abs(state.homeScore - state.awayScore) /
    Math.sqrt(secondsRemaining(state.period, state.clockSec) + 1),
  minutesLeft: ({ state }) => secondsRemaining(state.period, state.clockSec) / 60,
  leverage: ({ leverage }) => leverage,
  overtime: ({ state }) => (state.period > REGULATION_PERIODS ? 1 : 0),
} satisfies Record<string, (x: ScoreInput) => number>;
export type MomentScoreFeature = keyof typeof SCORE_FEATURES;
export const MOMENT_SCORE_FEATURES = Object.keys(SCORE_FEATURES) as MomentScoreFeature[];

export function momentScoreFeature(name: string, x: ScoreInput): number {
  if (!Object.hasOwn(SCORE_FEATURES, name))
    throw new Error(`unknown moment-score feature "${name}"`);
  return SCORE_FEATURES[name as MomentScoreFeature](x);
}

export function assertDetectorFeatures(d: DetectorModel): void {
  if (d.features.length !== d.coefficients.length)
    throw new Error(`detector ${d.version}: feature/coefficient mismatch`);
  for (const f of d.features)
    if (!Object.hasOwn(SCORE_FEATURES, f))
      throw new Error(`detector ${d.version}: unknown feature "${f}"`);
}

export const isEndgame = (s: Pick<GameState, 'period'>): boolean => s.period >= REGULATION_PERIODS;

/** Moment score in [0,1] for a live Q4/OT state; 0 elsewhere. */
export function momentScore(detector: DetectorModel, s: GameState, lev: number): number {
  if (s.status !== 'live' || !isEndgame(s)) return 0;
  return sigmoid(
    linearScore(detector.intercept, detector.features, detector.coefficients, (n) =>
      momentScoreFeature(n, { state: s, leverage: lev }),
    ),
  );
}

export function inFireWindow(
  detector: Pick<DetectorModel, 'maxSecondsRemaining'>,
  s: Pick<GameState, 'status' | 'period' | 'clockSec'>,
): boolean {
  if (s.status !== 'live' || !isEndgame(s)) return false;
  return s.period > REGULATION_PERIODS || s.clockSec <= detector.maxSecondsRemaining;
}

// ───────────── Reasons: human-readable, built only from real state ─────────────

const pct = (p: number) => `${Math.round(p * 100)}%`;

/** "3-point game, 2:18 left in Q4" / "Tied, 0:45 left in OT". */
export function closenessReason(
  s: Pick<GameState, 'period' | 'clockSec' | 'homeScore' | 'awayScore'>,
): string {
  const m = Math.abs(s.homeScore - s.awayScore);
  const [label, clock] = formatClock(s.period, s.clockSec).split(' ');
  return `${m === 0 ? 'Tied' : `${m}-point game`}, ${clock} left in ${label}`;
}

export type ScoringPlay = { elapsed: number; side: Side; points: number };
export const RUN_WINDOW_SECONDS = 240;

/** "HOU on a 10–2 run" over the last 4:00 of game clock, if one side leads it by 6+. */
export function runReason(
  plays: readonly ScoringPlay[],
  nowElapsed: number,
  meta: GameMeta,
): string | undefined {
  let home = 0;
  let away = 0;
  for (const p of plays) {
    if (p.elapsed < nowElapsed - RUN_WINDOW_SECONDS) continue;
    if (p.side === 'home') home += p.points;
    else away += p.points;
  }
  if (Math.abs(home - away) < 6) return undefined;
  const [side, hi, lo] =
    home > away ? (['home', home, away] as const) : (['away', away, home] as const);
  return `${meta[side].tricode} on a ${hi}–${lo} run`;
}

function topScorerReason(s: GameState): string | undefined {
  const h = topScorer(s, 'home');
  const a = topScorer(s, 'away');
  const best = !h ? a : !a ? h : h.points >= a.points ? h : a;
  return best && best.points >= 20 ? `${best.name} has ${best.points}` : undefined;
}

function comebackReason(s: GameState, meta: GameMeta): string | undefined {
  const m = s.homeScore - s.awayScore;
  for (const side of ['home', 'away'] as const) {
    const deficitNow = side === 'home' ? -m : m;
    const was = s.maxDeficit[side];
    if (was >= 10 && deficitNow <= 5 && was - deficitNow >= 8)
      return `${meta[side].tricode} was down ${was}`;
  }
  return undefined;
}

// ───────────── The stateful per-game detector ─────────────

export type MomentDetector = {
  observe(state: GameState, point: ProbabilityPoint, event: GameEvent): MomentDecision;
};

export function createMomentDetector(
  meta: GameMeta,
  winProb: WinProbModel,
  detector: DetectorModel,
): MomentDetector {
  let fired: MomentDecision | undefined;
  let prev = { home: 0, away: 0 };
  const plays: ScoringPlay[] = [];

  return {
    observe(state, point, event) {
      const now = elapsedSeconds(state.period, state.clockSec);
      for (const side of ['home', 'away'] as const) {
        const pts = (side === 'home' ? state.homeScore : state.awayScore) - prev[side];
        if (pts > 0) plays.push({ elapsed: now, side, points: pts });
      }
      prev = { home: state.homeScore, away: state.awayScore };

      const lev = leverage(winProb, state);
      const score = momentScore(detector, state, lev);
      const isMoment = !fired && inFireWindow(detector, state) && score >= detector.fireThreshold;

      const reasons: string[] = [];
      if (!isEndgame(state) || state.status !== 'live') {
        reasons.push(
          `${formatClock(state.period, state.clockSec)}: detector watches Q4 and overtime only`,
        );
      } else {
        reasons.push(closenessReason(state));
        const fav: Side = point.pHome >= 0.5 ? 'home' : 'away';
        reasons.push(
          `${meta[fav].tricode} ${pct(fav === 'home' ? point.pHome : 1 - point.pHome)} to win`,
        );
        if (lev >= 0.05) reasons.push(`One basket swings it ±${pct(lev / 2)}`);
        for (const r of [
          topScorerReason(state),
          runReason(plays, now, meta),
          comebackReason(state, meta),
        ])
          if (r) reasons.push(r);
        if (fired) reasons.push(`Already fired at ${formatClock(fired.period, fired.clockSec)}`);
        else if (!isMoment) {
          reasons.push(
            inFireWindow(detector, state)
              ? `Moment score ${score.toFixed(2)} (fires at ${detector.fireThreshold.toFixed(2)})`
              : `Window opens at ${formatClock(REGULATION_PERIODS, detector.maxSecondsRemaining).split(' ')[1]} in Q4`,
          );
        }
      }

      const decision: MomentDecision = {
        momentId: `${meta.gameId}:m${event.seq}`,
        gameId: meta.gameId,
        eventId: event.eventId,
        seq: event.seq,
        period: state.period,
        clockSec: state.clockSec,
        score,
        isMoment,
        reasons,
        detectorVersion: detector.version,
      };
      if (isMoment) fired = decision;
      return decision;
    },
  };
}
